import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API, apiError } from "../lib/api";
import { ArrowLeft, Save, ScanLine, Pill, Search, Plus, X, Sparkles, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "../components/BarcodeScanner";

const EMPTY = {
  name: "", company: "", batch_number: "",
  manufacturing_date: "", expiry_date: "",
  mrp: "", purchase_price: "", quantity: "",
  location: "", barcode: "", low_stock_threshold: 10,
  composition: [],
};

const UNITS = ["mg", "mcg", "g", "ml", "iu", "%"];
const EMPTY_SALT = { salt: "", strength: "", unit: "mg" };

export default function StockForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [matchedExisting, setMatchedExisting] = useState(null);
  const [scanState, setScanState] = useState(null); // null | 'searching' | 'matched' | 'new'
  const barcodeRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    if (!isEdit) {
      // auto-focus barcode field for USB scanners
      setTimeout(() => barcodeRef.current?.focus(), 200);
      return;
    }
    API.get(`/medicines/${id}`).then(({ data }) => {
      setForm({
        name: data.name, company: data.company, batch_number: data.batch_number,
        manufacturing_date: data.manufacturing_date, expiry_date: data.expiry_date,
        mrp: data.mrp, purchase_price: data.purchase_price, quantity: data.quantity,
        location: data.location || "", barcode: data.barcode || "",
        low_stock_threshold: data.low_stock_threshold || 10,
        composition: Array.isArray(data.composition) ? data.composition.map((c) => ({
          salt: c.salt || "",
          strength: c.strength !== null && c.strength !== undefined ? String(c.strength) : "",
          unit: c.unit || "mg",
        })) : [],
      });
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id, isEdit]);

  // When the barcode field changes (typed by USB scanner OR camera), look up existing medicine
  const onBarcodeChange = async (val) => {
    setForm((f) => ({ ...f, barcode: val }));
    setMatchedExisting(null);
    if (val.length < 4 || isEdit) { setScanState(null); return; }
    setScanState("searching");
    try {
      const { data } = await API.get("/medicines/search", { params: { q: val, limit: 5 } });
      const exact = (data || []).find((m) => m.barcode === val);
      if (exact) {
        // INSTANT auto-fill — no extra click needed
        setForm({
          name: exact.name,
          company: exact.company,
          batch_number: exact.batch_number,
          manufacturing_date: exact.manufacturing_date,
          expiry_date: exact.expiry_date,
          mrp: String(exact.mrp),
          purchase_price: String(exact.purchase_price),
          quantity: "",
          location: exact.location || "",
          barcode: exact.barcode || val,
          low_stock_threshold: exact.low_stock_threshold || 10,
          composition: Array.isArray(exact.composition) ? exact.composition.map((c) => ({
            salt: c.salt || "",
            strength: c.strength !== null && c.strength !== undefined ? String(c.strength) : "",
            unit: c.unit || "mg",
          })) : [],
        });
        setMatchedExisting(exact);
        setScanState("matched");
        toast.success(`Matched: ${exact.name}. Update qty/batch and save.`);
      } else {
        setScanState("new");
        toast.info(`New barcode ${val} — fill in the medicine details below.`);
        setTimeout(() => nameRef.current?.focus(), 100);
      }
    } catch {
      setScanState(null);
    }
  };

  const onScanResult = (code) => {
    onBarcodeChange(code);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const composition = (form.composition || [])
        .filter((c) => (c.salt || "").trim().length > 0)
        .map((c) => ({
          salt: c.salt.trim(),
          strength: c.strength !== "" && c.strength !== null ? parseFloat(c.strength) : null,
          unit: c.unit || null,
        }));
      const payload = {
        ...form,
        mrp: parseFloat(form.mrp),
        purchase_price: parseFloat(form.purchase_price),
        quantity: parseInt(form.quantity),
        low_stock_threshold: parseInt(form.low_stock_threshold || 10),
        composition,
      };
      if (isEdit) {
        await API.put(`/medicines/${id}`, payload);
        toast.success("Medicine updated");
      } else {
        await API.post("/medicines", payload);
        toast.success("Medicine added");
      }
      nav("/stock");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // Composition row helpers
  const addSaltRow = () => setForm((f) => ({ ...f, composition: [...(f.composition || []), { ...EMPTY_SALT }] }));
  const removeSaltRow = (i) => setForm((f) => ({ ...f, composition: (f.composition || []).filter((_, j) => j !== i) }));
  const updateSaltRow = (i, patch) => setForm((f) => ({
    ...f,
    composition: (f.composition || []).map((c, j) => (j === i ? { ...c, ...patch } : c)),
  }));
  const [suggestBusy, setSuggestBusy] = useState(false);
  const suggestFromName = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Enter the medicine name first"); return; }
    setSuggestBusy(true);
    try {
      const { data } = await API.post("/medicines/suggest-composition", { name });
      const rows = (data.composition || []).map((c) => ({
        salt: c.salt || "",
        strength: c.strength !== null && c.strength !== undefined ? String(c.strength) : "",
        unit: c.unit || "mg",
      }));
      if (rows.length === 0) {
        toast.info(data.note || "Could not auto-detect salts — please add them manually.");
      } else {
        setForm((f) => ({ ...f, composition: rows }));
        toast.success(`Suggested ${rows.length} salt(s) from ${data.source === "wikipedia" ? "Wikipedia" : "the name"}`);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSuggestBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-4xl page-enter" data-testid="stock-form-page">
      <Link to="/stock" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7] mb-3" data-testid="back-to-stock">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stock
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#1D4ED8] text-white flex items-center justify-center">
            <Pill className="w-6 h-6" />
          </div>
          <div>
            <div className="overline mb-1">Inventory</div>
            <h1 className="font-display text-3xl font-bold">{isEdit ? "Edit Medicine" : "Add Medicine to Stock"}</h1>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* Barcode strip */}
        <div className="bg-white border-2 border-dashed border-[#002FA7]/30 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline flex items-center gap-1.5"><ScanLine className="w-3 h-3" /> Barcode / QR</div>
              <div className="text-sm text-slate-600 mt-1">Scan with camera, USB scanner, or type manually.</div>
            </div>
            <BarcodeScanner
              label="Scan with Camera"
              className="btn-primary"
              onResult={onScanResult}
            />
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={barcodeRef}
              data-testid="barcode-input"
              value={form.barcode}
              onChange={(e) => onBarcodeChange(e.target.value)}
              placeholder="Place cursor here and scan, or type the barcode…"
              autoComplete="off"
              className="w-full pl-9 pr-3 py-3 border border-slate-200 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
            />
          </div>

          {scanState === "matched" && matchedExisting && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3" data-testid="barcode-match">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 font-bold">✓</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-emerald-800">Match found — form auto-filled</div>
                <div className="text-xs text-emerald-700 mt-0.5 break-words">
                  {matchedExisting.name} · {matchedExisting.company} · Batch {matchedExisting.batch_number} · Existing stock: {matchedExisting.quantity}
                </div>
                <div className="text-xs text-emerald-600 mt-1">
                  Just enter the new <b>Quantity</b> (and update batch/expiry if it's a fresh batch) then save.
                </div>
              </div>
            </div>
          )}

          {scanState === "new" && form.barcode && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3" data-testid="barcode-new">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 font-bold">!</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-800">New barcode — not in your stock yet</div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Pharmacy barcodes only carry an ID — they don't include the medicine name or manufacturer. Please fill in the details below; this barcode will be saved with the medicine and recognised automatically next time.
                </div>
              </div>
            </div>
          )}

          {scanState === "searching" && (
            <div className="mt-3 text-xs text-slate-500">Searching stock for this barcode…</div>
          )}
        </div>

        {/* Fields */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Field label="Medicine Name *">
            <input ref={nameRef} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="med-name-input" className="inp" />
          </Field>
          <Field label="Company / Manufacturer *">
            <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="med-company-input" className="inp" />
          </Field>
          <Field label="Batch Number">
            <input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="med-batch-input" className="inp font-mono" placeholder="Optional" />
          </Field>
          <Field label="Rack / Location">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="med-location-input" className="inp" placeholder="e.g. A3" />
          </Field>
          <Field label="Manufacturing Date">
            <input type="date" value={form.manufacturing_date} onChange={(e) => setForm({ ...form, manufacturing_date: e.target.value })} data-testid="med-mfg-input" className="inp" />
          </Field>
          <Field label="Expiry Date *">
            <input required type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="med-expiry-input" className="inp" />
          </Field>
          <Field label="MRP (₹) *">
            <input required type="number" step="0.01" min="0" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="med-mrp-input" className="inp" />
          </Field>
          <Field label="Purchase Price (₹) *">
            <input required type="number" step="0.01" min="0" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="med-cost-input" className="inp" />
          </Field>
          <Field label="Quantity *">
            <input required type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="med-qty-input" className="inp" />
          </Field>
          <Field label="Low Stock Threshold">
            <input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="inp" />
          </Field>
        </div>

        {/* Composition / Salt details */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="composition-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-[#10B981]" />
              <div>
                <div className="font-semibold text-sm">Salt Composition</div>
                <div className="text-xs text-slate-500">Used to suggest equivalent medicines when this one is out of stock.</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={suggestFromName} disabled={suggestBusy}
                      className="btn-ghost text-xs gap-1.5" data-testid="suggest-composition-btn">
                <Sparkles className="w-3.5 h-3.5" /> {suggestBusy ? "Looking…" : "Suggest from name"}
              </button>
              <button type="button" onClick={addSaltRow} className="btn-secondary text-xs gap-1.5" data-testid="add-salt-row">
                <Plus className="w-3.5 h-3.5" /> Add salt
              </button>
            </div>
          </div>
          {(form.composition || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3" data-testid="no-composition">
              No salts added yet. Click <b>Suggest from name</b> or <b>Add salt</b>.
            </div>
          ) : (
            <div className="space-y-2">
              {form.composition.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`salt-row-${i}`}>
                  <input
                    value={c.salt}
                    onChange={(e) => updateSaltRow(i, { salt: e.target.value })}
                    placeholder="Salt name (e.g. Paracetamol)"
                    data-testid={`salt-name-${i}`}
                    className="inp col-span-6"
                  />
                  <input
                    type="number" step="0.01" min="0"
                    value={c.strength}
                    onChange={(e) => updateSaltRow(i, { strength: e.target.value })}
                    placeholder="Strength"
                    data-testid={`salt-strength-${i}`}
                    className="inp col-span-3"
                  />
                  <select
                    value={c.unit || "mg"}
                    onChange={(e) => updateSaltRow(i, { unit: e.target.value })}
                    data-testid={`salt-unit-${i}`}
                    className="inp col-span-2"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <button type="button" onClick={() => removeSaltRow(i)}
                          className="col-span-1 p-2 text-slate-400 hover:text-red-600"
                          data-testid={`remove-salt-${i}`} aria-label="Remove salt">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 sticky bottom-0 bg-[#F8FAFC] py-3 -mx-6 md:-mx-8 px-6 md:px-8 border-t border-slate-200">
          <Link to="/stock" className="btn-ghost">Cancel</Link>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="save-stock-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : isEdit ? "Update Medicine" : "Save to Stock"}
          </button>
        </div>
      </form>

      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.55rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
